import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactSlider from 'react-slider';

import CategorySelectorGrid from 'components/CategorySelectorGrid';
import { getUtilityLabel } from 'utils/utilities';
import { Modal, ModalTitle, ModalFoot, Switch } from 'components/Modal';

const FiltersModal = ({
  isOpen,
  isBrandSearch,
  onClose,
  onApplyFilters,
  categories,
  utilities,
  initialFilters,
  isAuthenticated,
}) => {
  const { t, i18n } = useTranslation();

  const DEFAULT_MAX_PRICE = 1000;
  const MAX_PRICE_LIMIT = 20000;
  const PRICE_STEP = 100;

  const [section, setSection] = useState('categories');
  const [selectedMacroCategories, setSelectedMacroCategories] = useState([]);
  const [selectedMicroCategories, setSelectedMicroCategories] = useState([]);
  const [selectedUtilities, setSelectedUtilities] = useState([]);
  const [maxPrice, setMaxPrice] = useState(DEFAULT_MAX_PRICE);
  const [priceFilterEnabled, setPriceFilterEnabled] = useState(false);
  const [hidePastEvents, setHidePastEvents] = useState(true);
  const [hideLiveEvents, setHideLiveEvents] = useState(false);
  const [monthRange, setMonthRange] = useState({ from: '', to: '' });
  const [showAllMicros, setShowAllMicros] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    // Category ids arrive as strings when they round-trip through the URL
    // (listings deep links); the selector grid compares against the numeric
    // catalog ids, so coerce + dedupe or nothing shows selected and
    // re-picking appends duplicates.
    const toIdList = (value) =>
      Array.isArray(value)
        ? [...new Set(value.map(Number).filter(Number.isFinite))]
        : [];

    setSelectedMacroCategories(toIdList(initialFilters?.macroCategories));
    setSelectedMicroCategories(toIdList(initialFilters?.microCategories));
    setSelectedUtilities(
      Array.isArray(initialFilters?.utilities)
        ? [...new Set(initialFilters.utilities)]
        : []
    );

    const hasPrice =
      typeof initialFilters?.maxPrice === 'number' &&
      Number.isFinite(initialFilters.maxPrice);
    setPriceFilterEnabled(hasPrice);
    setMaxPrice(hasPrice ? initialFilters.maxPrice : DEFAULT_MAX_PRICE);

    setHidePastEvents(
      initialFilters?.hidePastEvents == null
        ? true
        : Boolean(initialFilters.hidePastEvents)
    );

    setHideLiveEvents(Boolean(initialFilters?.hideLiveEvents));

    const isYm = (v) =>
      typeof v === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
    setMonthRange({
      from: isYm(initialFilters?.monthRange?.from)
        ? initialFilters.monthRange.from
        : '',
      to: isYm(initialFilters?.monthRange?.to)
        ? initialFilters.monthRange.to
        : '',
    });

    setShowAllMicros(false);
    setSection('categories');
  }, [isOpen, initialFilters]);

  const handleApply = () => {
    onApplyFilters({
      macroCategories: selectedMacroCategories,
      microCategories: selectedMicroCategories,
      utilities: selectedUtilities,
      maxPrice: priceFilterEnabled ? maxPrice : null,
      hidePastEvents,
      hideLiveEvents,
      monthRange,
    });
    onClose();
  };

  const handleClear = () => {
    setSelectedMacroCategories([]);
    setSelectedMicroCategories([]);
    setSelectedUtilities([]);
    setMaxPrice(DEFAULT_MAX_PRICE);
    setPriceFilterEnabled(false);
    setHidePastEvents(true);
    setHideLiveEvents(false);
    setMonthRange({ from: '', to: '' });
    setShowAllMicros(false);
  };

  const clampPrice = (value) => {
    if (value < 0) return 0;
    if (value > MAX_PRICE_LIMIT) return MAX_PRICE_LIMIT;
    return value;
  };

  const handleManualPriceChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setMaxPrice(0);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    setMaxPrice(clampPrice(Math.round(parsed)));
  };

  const toggleUtility = (utilityId) => {
    setSelectedUtilities((prev) =>
      prev.includes(utilityId)
        ? prev.filter((u) => u !== utilityId)
        : [...prev, utilityId]
    );
  };

  const hasUtilities = Array.isArray(utilities) && utilities.length > 0;

  const counts = useMemo(
    () => ({
      categories:
        selectedMacroCategories.length + selectedMicroCategories.length,
      dates: (monthRange?.from ? 1 : 0) + (monthRange?.to ? 1 : 0),
      events: (hidePastEvents ? 1 : 0) + (hideLiveEvents ? 1 : 0),
      price: priceFilterEnabled ? 1 : 0,
      utilities: selectedUtilities.length,
    }),
    [
      selectedMacroCategories,
      selectedMicroCategories,
      monthRange,
      hidePastEvents,
      hideLiveEvents,
      priceFilterEnabled,
      selectedUtilities,
    ]
  );

  const sections = useMemo(() => {
    const list = [
      { id: 'categories', label: t('listing.categories', 'Categories') },
    ];
    if (!isBrandSearch) {
      list.push({ id: 'dates', label: t('listing.between_dates', 'Between dates') });
      list.push({ id: 'events', label: t('filters.events', 'Event status') });
      if (isAuthenticated) {
        list.push({ id: 'price', label: t('listing.set_max_price', 'Max price') });
      }
      if (hasUtilities) {
        list.push({ id: 'utilities', label: t('listing.utilities', 'Utilities') });
      }
    }
    return list;
  }, [isBrandSearch, isAuthenticated, hasUtilities, t]);

  const total = Object.entries(counts)
    .filter(([id]) => sections.some((s) => s.id === id))
    .reduce((a, [, v]) => a + v, 0);

  const activeSection = sections.some((s) => s.id === section)
    ? section
    : 'categories';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      className="h-[min(820px,92vh)]"
    >
      {/* Head */}
      <div className="px-5 sm:px-8 py-4 sm:py-5 border-b border-line bg-paper flex items-center gap-3 sm:gap-4">
        <div className="hidden sm:block">
          <ModalTitle size="sm">
            {t('filters.title_a', 'Refine')}{' '}
            <em className="italic text-yellow-04">
              {t('filters.title_b', 'your view')}
            </em>
          </ModalTitle>
        </div>
        {total > 0 && (
          <span className="inline-flex items-center justify-center min-w-7 h-7 rounded-full bg-ink text-white text-[13px] font-display font-bold px-2">
            {total}
          </span>
        )}
        <div className="ml-auto flex gap-2.5 pr-10">
          <button
            type="button"
            onClick={handleClear}
            className="rounded-full px-3.5 py-2 text-[13px] font-semibold text-ink hover:bg-ink/5 transition"
          >
            {t('listing.clear', 'Clear all')}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="hidden sm:inline-flex rounded-full bg-ink text-white text-[13px] font-semibold px-3.5 py-2 hover:bg-black transition"
          >
            {t('listing.apply', 'Apply filters')}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 grid-rows-[auto_minmax(0,1fr)] sm:grid-cols-[220px_1fr] sm:grid-rows-[minmax(0,1fr)] flex-1 min-h-0">
        {/* Section nav */}
        <div className="flex sm:flex-col gap-1 overflow-x-auto sm:overflow-y-auto p-3 sm:py-[22px] sm:px-3 border-b sm:border-b-0 sm:border-r border-line bg-paper-2">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={`flex items-center justify-between gap-2 shrink-0 sm:w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] transition ${
                activeSection === s.id
                  ? 'bg-white text-ink font-bold shadow-soft'
                  : 'text-muted-2 font-medium hover:bg-ink/[0.04] hover:text-ink'
              }`}
            >
              <span className="whitespace-nowrap">{s.label}</span>
              {counts[s.id] > 0 && (
                <span className="text-[11px] font-bold bg-brand-soft text-yellow-04 px-[7px] py-0.5 rounded-full">
                  {counts[s.id]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 sm:px-9 py-7">
          {activeSection === 'categories' && (
            <div>
              <div className="flex items-baseline justify-between mb-3.5">
                <h3 className="font-display font-bold text-[15px] text-ink">
                  {t('listing.categories', 'Categories')}
                </h3>
                {counts.categories > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMacroCategories([]);
                      setSelectedMicroCategories([]);
                    }}
                    className="text-xs text-muted-2 underline underline-offset-[3px] hover:text-ink"
                  >
                    {t('listing.clear', 'Clear')}
                  </button>
                )}
              </div>
              <p className="text-[12.5px] text-muted-2 mb-3.5">
                {t(
                  'filters.section.categories.hint',
                  "Pick what kind of brand you're looking for — multiple is fine."
                )}
              </p>
              <CategorySelectorGrid
                noTitle
                categories={categories}
                selectedMacros={selectedMacroCategories}
                setSelectedMacros={setSelectedMacroCategories}
                selectedMicros={selectedMicroCategories}
                setSelectedMicros={setSelectedMicroCategories}
                showAllMicros={showAllMicros}
                setShowAllMicros={setShowAllMicros}
              />
            </div>
          )}

          {activeSection === 'dates' && (
            <div>
              <div className="flex items-baseline justify-between mb-3.5">
                <h3 className="font-display font-bold text-[15px] text-ink">
                  {t('listing.between_dates', 'Between dates')}
                </h3>
                {counts.dates > 0 && (
                  <button
                    type="button"
                    onClick={() => setMonthRange({ from: '', to: '' })}
                    className="text-xs text-muted-2 underline underline-offset-[3px] hover:text-ink"
                  >
                    {t('listing.clear', 'Clear')}
                  </button>
                )}
              </div>
              <p className="text-[12.5px] text-muted-2 mb-3.5">
                {t(
                  'listing.between_dates_hint',
                  'Filter events by month and year range'
                )}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <label className="flex flex-col gap-1 border border-line bg-white rounded-xl px-3.5 py-3">
                  <span className="font-display text-[11px] uppercase tracking-[0.1em] font-bold text-muted">
                    {t('listing.from_month', 'From')}
                  </span>
                  <input
                    type="month"
                    value={monthRange?.from || ''}
                    max={monthRange?.to || undefined}
                    onChange={(e) =>
                      setMonthRange({
                        from: e.target.value,
                        to: monthRange?.to || '',
                      })
                    }
                    className="bg-transparent font-display text-[17px] font-bold text-ink focus:outline-none"
                  />
                </label>
                <label className="flex flex-col gap-1 border border-line bg-white rounded-xl px-3.5 py-3">
                  <span className="font-display text-[11px] uppercase tracking-[0.1em] font-bold text-muted">
                    {t('listing.to_month', 'To')}
                  </span>
                  <input
                    type="month"
                    value={monthRange?.to || ''}
                    min={monthRange?.from || undefined}
                    onChange={(e) =>
                      setMonthRange({
                        from: monthRange?.from || '',
                        to: e.target.value,
                      })
                    }
                    className="bg-transparent font-display text-[17px] font-bold text-ink focus:outline-none"
                  />
                </label>
              </div>
            </div>
          )}

          {activeSection === 'events' && (
            <div>
              <h3 className="font-display font-bold text-[15px] text-ink mb-3.5">
                {t('filters.events', 'Event status')}
              </h3>
              <p className="text-[12.5px] text-muted-2 mb-3.5">
                {t(
                  'filters.section.events.hint',
                  'Hide events that are already over, cancelled or in progress.'
                )}
              </p>
              <div className="flex items-center justify-between gap-4 px-4 py-3.5 border border-line bg-white rounded-xl">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink">
                    {t(
                      'listing.hide_past_events',
                      'Hide past and cancelled events'
                    )}
                  </div>
                  <div className="text-[12.5px] text-muted-2 mt-0.5">
                    {t(
                      'listing.hide_past_events_hint',
                      'Only show events happening now or in the future, and hide cancelled ones'
                    )}
                  </div>
                </div>
                <Switch
                  checked={hidePastEvents}
                  onChange={setHidePastEvents}
                  ariaLabel={t(
                    'listing.hide_past_events',
                    'Hide past and cancelled events'
                  )}
                />
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3.5 border border-line bg-white rounded-xl mt-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink">
                    {t('listing.hide_live_events', 'Hide live events')}
                  </div>
                  <div className="text-[12.5px] text-muted-2 mt-0.5">
                    {t(
                      'listing.hide_live_events_hint',
                      'Hide events that are currently happening'
                    )}
                  </div>
                </div>
                <Switch
                  checked={hideLiveEvents}
                  onChange={setHideLiveEvents}
                  ariaLabel={t('listing.hide_live_events', 'Hide live events')}
                />
              </div>
            </div>
          )}

          {activeSection === 'price' && (
            <div>
              <h3 className="font-display font-bold text-[15px] text-ink mb-3.5">
                {t('listing.set_max_price', 'Max price per day')}
              </h3>
              <p className="text-[12.5px] text-muted-2 mb-3.5">
                {t(
                  'listing.set_max_price_hint',
                  'Only show events at or below the selected price'
                )}
              </p>
              <div className="flex items-center justify-between gap-4 px-4 py-3.5 border border-line bg-white rounded-xl">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink">
                    {t('filters.section.price.toggle', 'Filter by price')}
                  </div>
                  <div className="text-[12.5px] text-muted-2 mt-0.5">
                    {t(
                      'filters.section.price.toggle_hint',
                      'Off shows all events regardless of price'
                    )}
                  </div>
                </div>
                <Switch
                  checked={priceFilterEnabled}
                  onChange={setPriceFilterEnabled}
                  ariaLabel={t(
                    'filters.section.price.toggle',
                    'Filter by price'
                  )}
                />
              </div>

              {priceFilterEnabled && (
                <div className="mt-7">
                  <div className="flex items-baseline justify-between mb-3.5">
                    <div className="font-display font-bold text-[26px] tracking-[-0.01em] text-ink">
                      <span className="text-sm text-muted mr-1">
                        {t('filters.up_to', 'up to')}
                      </span>
                      €{Number(maxPrice || 0).toLocaleString()}
                    </div>
                    <label className="flex items-center gap-2 text-xs text-muted-2">
                      <span>{t('listing.max_price', 'Max price')}</span>
                      <input
                        type="number"
                        min={0}
                        max={MAX_PRICE_LIMIT}
                        step={1}
                        value={maxPrice}
                        onChange={handleManualPriceChange}
                        className="w-24 px-2 py-1 text-sm font-normal border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/40"
                        aria-label={t('listing.max_price', 'Max price')}
                      />
                    </label>
                  </div>
                  <ReactSlider
                    min={0}
                    max={MAX_PRICE_LIMIT}
                    step={PRICE_STEP}
                    value={clampPrice(Number(maxPrice) || 0)}
                    onChange={setMaxPrice}
                    className="react-slider relative h-1.5 rounded-full bg-line my-6 mx-2"
                    thumbClassName="react-thumb h-[22px] w-[22px] -top-2 bg-white rounded-full border-2 border-ink shadow-md cursor-pointer focus:outline-none"
                    trackClassName="react-track"
                    renderTrack={(props, state) => (
                      <div
                        {...props}
                        className={`absolute h-1.5 rounded-full ${
                          state.index === 1 ? 'bg-transparent' : 'bg-ink'
                        }`}
                      />
                    )}
                    renderThumb={(props, state) => (
                      <div {...props}>
                        <span className="absolute -top-9 left-1/2 -translate-x-1/2 bg-ink text-white text-[11.5px] font-bold font-display px-2 py-1 rounded-md whitespace-nowrap">
                          €{state.valueNow.toLocaleString()}
                        </span>
                      </div>
                    )}
                  />
                  <div className="flex justify-between text-[11px] text-muted font-display mt-2">
                    <span>€0</span>
                    <span>€{MAX_PRICE_LIMIT.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'utilities' && (
            <div>
              <div className="flex items-baseline justify-between mb-3.5">
                <h3 className="font-display font-bold text-[15px] text-ink">
                  {t('listing.utilities', 'Utilities & amenities')}
                </h3>
                {counts.utilities > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedUtilities([])}
                    className="text-xs text-muted-2 underline underline-offset-[3px] hover:text-ink"
                  >
                    {t('listing.clear', 'Clear')}
                  </button>
                )}
              </div>
              <p className="text-[12.5px] text-muted-2 mb-3.5">
                {t(
                  'filters.section.utilities.hint',
                  'Only show spaces that have all the selected utilities.'
                )}
              </p>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">
                {(utilities || []).map((utility) => {
                  const id = typeof utility === 'string' ? utility : utility.id;
                  const label =
                    typeof utility === 'string'
                      ? utility
                      : getUtilityLabel(id, i18n.language);
                  const on = selectedUtilities.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleUtility(id)}
                      title={label}
                      className={`text-left text-[13px] font-semibold px-3.5 py-3 rounded-xl border transition ${
                        on
                          ? 'bg-ink text-white border-ink'
                          : 'bg-white text-ink-2 border-line hover:bg-paper-2'
                      }`}
                    >
                      <span className="line-clamp-2 break-words leading-tight">
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <ModalFoot>
        <div className="text-[13.5px] text-muted-2">
          {total > 0 ? (
            <>
              {t('filters.summary_count', '{{k}} filter applied', {
                k: total,
                count: total,
              })}
            </>
          ) : (
            t('filters.summary_empty_short', 'No filters applied yet')
          )}
        </div>
        <div className="ml-auto flex gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-line-strong bg-white px-5 py-3 text-sm font-semibold hover:bg-paper-2 transition"
          >
            {t('filters.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="rounded-full bg-ink text-white px-5 py-3 text-sm font-semibold hover:bg-black transition"
          >
            {t('listing.apply', 'Apply filters')}
          </button>
        </div>
      </ModalFoot>
    </Modal>
  );
};

export default FiltersModal;
